import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Avatar,
  Divider,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
  useTheme,
  alpha,
  Stack,
  Tooltip,
  Menu,
  ButtonGroup,
  Tabs,
  Tab,
  ImageList,
  ImageListItem,
  Autocomplete,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Reply as ReplyIcon,
  Visibility as ViewIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  FiberNew as NewIcon,
  MarkEmailRead as ReadIcon,
  Archive as ArchiveIcon,
  MoreVert as MoreVertIcon,
  DoneAll as DoneAllIcon,
  Image as ImageIcon,
  Link as LinkIcon,
  PersonAdd as PersonAddIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as CopyIcon,
  PhotoLibrary as GalleryIcon
} from '@mui/icons-material';
import { UseApi } from '../Hooks/UseApi';
import { UseAuthRoles } from '../Hooks/UseAuthRoles';
import { useNavigate } from 'react-router-dom';

const TattooInquiries = () => {
  const theme = useTheme();
  const api = UseApi();
  const navigate = useNavigate();
  const { hasRole } = UseAuthRoles();
  const apiRef = useRef(api);
  
  const [loading, setLoading] = useState(true);
  const [inquiries, setInquiries] = useState([]);
  const [error, setError] = useState('');
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Tab state for filtering
  const [activeTab, setActiveTab] = useState(0);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const pageSize = 10;
  
  // Filter state
  const [filterType, setFilterType] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Menu state for actions
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuInquiry, setActionMenuInquiry] = useState(null);
  
  // Link to client dialog
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkingInquiry, setLinkingInquiry] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [loadingClients, setLoadingClients] = useState(false);
  const [linkMode, setLinkMode] = useState('existing'); // 'existing' or 'new'
  
  // Image preview dialog
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Tab definitions with counts
  const tabs = useMemo(() => [
    { label: 'All', value: 'all' },
    { label: 'New', value: 'new' },
    { label: 'Read', value: 'read' },
    { label: 'Replied', value: 'replied' },
    { label: 'Archived', value: 'archived' }
  ], []);

  useEffect(() => {
    fetchInquiries();
  }, [currentPage, filterType, sortBy, statusFilter, activeTab]);

  const fetchInquiries = useCallback(async () => {
    if (!api) return;
    
    try {
      setLoading(true);
      setError('');
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: pageSize.toString()
      });
      
      // Use tab status filter
      const tabStatus = tabs[activeTab]?.value;
      if (tabStatus && tabStatus !== 'all') {
        params.append('status', tabStatus);
      } else if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      const response = await apiRef.current.fetchWithAuth(`/contact/inquiries/?${params}`);
      
      setInquiries(response.inquiries || []);
      setTotalCount(response.total_count || 0);
      setHasNext(response.has_next || false);
      
    } catch (err) {
      setError('Failed to load inquiries: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, api, statusFilter, activeTab, tabs]);

  const fetchClients = useCallback(async (query = '') => {
    try {
      setLoadingClients(true);
      const params = new URLSearchParams();
      if (query) {
        params.append('search', query);
      }
      params.append('limit', '20');
      
      const response = await apiRef.current.fetchWithAuth(`/clients/?${params}`);
      
      // Ensure we always set an array
      let clientsArray = [];
      if (Array.isArray(response)) {
        clientsArray = response;
      } else if (response && Array.isArray(response.results)) {
        clientsArray = response.results;
      } else if (response && Array.isArray(response.clients)) {
        clientsArray = response.clients;
      }
      
      setClients(clientsArray);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setClients([]); // Ensure empty array on error
    } finally {
      setLoadingClients(false);
    }
  }, []);

  const handleStatusUpdate = async (inquiryId, newStatus) => {
    try {
      await apiRef.current.fetchWithAuth(`/contact/inquiries/${inquiryId}/`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus })
      });
      
      fetchInquiries();
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update status');
    }
  };

  const handleMarkAsRead = async (inquiryId) => {
    try {
      await apiRef.current.fetchWithAuth(`/contact/inquiries/${inquiryId}/mark-read/`, {
        method: 'POST'
      });
      
      fetchInquiries();
    } catch (err) {
      console.error('Error marking as read:', err);
      setError('Failed to mark as read');
    }
  };

  const handleMarkAsReplied = async (inquiryId) => {
    try {
      await apiRef.current.fetchWithAuth(`/contact/inquiries/${inquiryId}/mark-replied/`, {
        method: 'POST'
      });
      
      fetchInquiries();
    } catch (err) {
      console.error('Error marking as replied:', err);
      setError('Failed to mark as replied');
    }
  };

  const handleArchive = async (inquiryId) => {
    try {
      await apiRef.current.fetchWithAuth(`/contact/inquiries/${inquiryId}/archive/`, {
        method: 'POST'
      });
      
      fetchInquiries();
      handleCloseActionMenu();
    } catch (err) {
      console.error('Error archiving inquiry:', err);
      setError('Failed to archive inquiry');
    }
  };

  const handleOpenActionMenu = (event, inquiry) => {
    setActionMenuAnchor(event.currentTarget);
    setActionMenuInquiry(inquiry);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuInquiry(null);
  };

  const handleViewDetails = async (inquiry) => {
    setSelectedInquiry(inquiry);
    setDialogOpen(true);
    
    if (!inquiry.is_read) {
      await handleMarkAsRead(inquiry.id);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedInquiry(null);
  };

  const handleReplyToClient = async (inquiry) => {
    await handleMarkAsReplied(inquiry.id);
    
    const subject = `Re: Your ${inquiry.inquiry_type === 'studio' ? 'Studio' : 'Tattoo'} Inquiry`;
    const body = `Hi ${inquiry.client_name},\n\nThank you for your interest! I'd love to discuss your project.\n\n`;
    window.location.href = `mailto:${inquiry.client_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  // FIXED: Link to client handlers - close detail dialog first
  const handleOpenLinkDialog = (inquiry) => {
    // Close the detail dialog first if it's open
    setDialogOpen(false);
    setSelectedInquiry(null);
    
    // Then open the link dialog with the inquiry
    setLinkingInquiry(inquiry);
    setLinkMode('existing');
    setSelectedClient(null);
    setLinkDialogOpen(true);
    fetchClients();
  };

  const handleCloseLinkDialog = () => {
    setLinkDialogOpen(false);
    setLinkingInquiry(null);
    setSelectedClient(null);
  };

  const handleLinkToClient = async () => {
    if (!linkingInquiry || !selectedClient) return;
    
    try {
      await apiRef.current.fetchWithAuth(`/contact/inquiries/${linkingInquiry.id}/link-client/`, {
        method: 'POST',
        body: JSON.stringify({ client_id: selectedClient.id })
      });
      
      handleCloseLinkDialog();
      fetchInquiries();
      
      // Show success message instead of navigating away
      setError(''); // Clear any errors
      // Could add a success snackbar here if desired
    } catch (err) {
      console.error('Error linking client:', err);
      setError('Failed to link inquiry to client');
    }
  };

  const handleCreateNewClient = async () => {
    if (!linkingInquiry) return;
    
    try {
      // Parse client name - handle single names
      const nameParts = linkingInquiry.client_name.trim().split(' ');
      const firstName = nameParts[0] || 'Unknown';
      // If only one name provided, use it for both or use a placeholder
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '-';
      
      // Create client from inquiry data - URL is /clients/add/ not /clients/
      const response = await apiRef.current.fetchWithAuth('/clients/add/', {
        method: 'POST',
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          email: linkingInquiry.client_email,
          phone_number: linkingInquiry.client_phone || '',
          notes: '',
          preferred_styles: '',
          referral_source: 'Inquiry Form'
        })
      });
      
      // Link the inquiry to the new client
      await apiRef.current.fetchWithAuth(`/contact/inquiries/${linkingInquiry.id}/link-client/`, {
        method: 'POST',
        body: JSON.stringify({ client_id: response.id })
      });
      
      handleCloseLinkDialog();
      fetchInquiries();
      
      // Show success instead of navigating
      setError(''); // Clear any errors
    } catch (err) {
      console.error('Error creating client:', err);
      setError('Failed to create client from inquiry');
    }
  };

  // Image preview handlers
  const handleOpenImagePreview = (images, startIndex = 0) => {
    setPreviewImages(images);
    setCurrentImageIndex(startIndex);
    setImagePreviewOpen(true);
  };

  const handleCloseImagePreview = () => {
    setImagePreviewOpen(false);
    setPreviewImages([]);
    setCurrentImageIndex(0);
  };

  const getStatusColor = (status) => {
    if (!status) return theme.palette.grey[500];
    const colors = {
      new: theme.palette.info.main,
      read: theme.palette.primary.main,
      replied: theme.palette.success.main,
      completed: theme.palette.success.dark,
      archived: theme.palette.grey[500]
    };
    return colors[status] || theme.palette.grey[500];
  };

  const getStatusIcon = (status) => {
    if (!status) return <NewIcon />;
    const icons = {
      new: <NewIcon />,
      read: <ReadIcon />,
      replied: <ReplyIcon />,
      completed: <DoneAllIcon />,
      archived: <ArchiveIcon />
    };
    return icons[status] || <PendingIcon />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 86400000) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diff < 604800000) {
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatBudget = (budget) => {
    const budgets = {
      under_200: 'Under $200',
      '200_500': '$200 - $500',
      '500_1000': '$500 - $1,000',
      '1000_2500': '$1,000 - $2,500',
      '2500_plus': '$2,500+',
      discuss: 'To discuss'
    };
    return budgets[budget] || budget || 'Not specified';
  };

  const formatTimeframe = (timeframe) => {
    const timeframes = {
      asap: 'ASAP',
      month: 'Within a month',
      '3_months': 'Within 3 months',
      '6_months': 'Within 6 months',
      flexible: 'Flexible'
    };
    return timeframes[timeframe] || timeframe || 'Not specified';
  };

  // Filter inquiries by search
  const filteredInquiries = useMemo(() => {
    if (!searchQuery) return inquiries;
    
    const query = searchQuery.toLowerCase();
    return inquiries.filter(inquiry => 
      inquiry.client_name?.toLowerCase().includes(query) ||
      inquiry.client_email?.toLowerCase().includes(query) ||
      inquiry.project_description?.toLowerCase().includes(query)
    );
  }, [inquiries, searchQuery]);

  if (loading && inquiries.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading inquiries...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Tattoo Inquiries
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage appointment requests and client inquiries from your public forms
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Tabs for status filtering */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, v) => { setActiveTab(v); setCurrentPage(1); }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((tab, index) => (
            <Tab key={tab.value} label={tab.label} />
          ))}
        </Tabs>
      </Paper>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by name, email, or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={filterType}
                label="Type"
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="all">All Inquiries</MenuItem>
                <MenuItem value="studio">Studio</MenuItem>
                <MenuItem value="artist">Artist</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate('/inquiry-form-settings')}
              sx={{ height: '100%' }}
            >
              Form Settings
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Inquiries List */}
      {filteredInquiries.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No inquiries found
          </Typography>
          <Typography color="text.secondary">
            {tabs[activeTab]?.value !== 'all' 
              ? `No ${tabs[activeTab]?.label?.toLowerCase()} inquiries`
              : 'Share your inquiry form to start receiving requests'}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {filteredInquiries.map((inquiry) => (
            <Card 
              key={inquiry.id}
              sx={{ 
                borderLeft: `4px solid ${getStatusColor(inquiry.status)}`,
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: theme.shadows[4],
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  {/* Avatar and Name */}
                  <Grid item xs={12} sm={4}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar 
                        sx={{ 
                          bgcolor: alpha(getStatusColor(inquiry.status), 0.2),
                          color: getStatusColor(inquiry.status)
                        }}
                      >
                        {inquiry.client_name?.charAt(0).toUpperCase() || '?'}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="600">
                          {inquiry.client_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {inquiry.client_email}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                  
                  {/* Project Preview */}
                  <Grid item xs={12} sm={4}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {inquiry.project_description}
                    </Typography>
                    
                    {/* Image indicator */}
                    {inquiry.reference_images?.length > 0 && (
                      <Chip
                        size="small"
                        icon={<GalleryIcon />}
                        label={`${inquiry.reference_images.length} image${inquiry.reference_images.length > 1 ? 's' : ''}`}
                        sx={{ mt: 1 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenImagePreview(inquiry.reference_images);
                        }}
                      />
                    )}
                  </Grid>
                  
                  {/* Status and Actions */}
                  <Grid item xs={12} sm={4}>
                    <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(inquiry.created_at)}
                      </Typography>
                      
                      <Chip
                        size="small"
                        icon={getStatusIcon(inquiry.status)}
                        label={inquiry.status ? inquiry.status.charAt(0).toUpperCase() + inquiry.status.slice(1) : 'New'}
                        sx={{
                          bgcolor: alpha(getStatusColor(inquiry.status), 0.1),
                          color: getStatusColor(inquiry.status)
                        }}
                      />
                      
                      {/* Linked client indicator */}
                      {inquiry.linked_client ? (
                        <Tooltip title={`Linked to ${inquiry.linked_client.name}`}>
                          <Chip
                            size="small"
                            icon={<LinkIcon />}
                            label="Linked"
                            color="success"
                            variant="outlined"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/clients/${inquiry.linked_client.id}`);
                            }}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Link to client">
                          <IconButton 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenLinkDialog(inquiry);
                            }}
                          >
                            <PersonAddIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      <ButtonGroup size="small">
                        <Button
                          onClick={() => handleViewDetails(inquiry)}
                          startIcon={<ViewIcon />}
                        >
                          View
                        </Button>
                        <IconButton 
                          size="small"
                          onClick={(e) => handleOpenActionMenu(e, inquiry)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </ButtonGroup>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Pagination */}
      {totalCount > pageSize && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(p => p - 1)}
          >
            Previous
          </Button>
          <Typography sx={{ mx: 2, alignSelf: 'center' }}>
            Page {currentPage} of {Math.ceil(totalCount / pageSize)}
          </Typography>
          <Button
            disabled={!hasNext}
            onClick={() => setCurrentPage(p => p + 1)}
          >
            Next
          </Button>
        </Box>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
      >
        <MenuItem onClick={() => {
          handleReplyToClient(actionMenuInquiry);
          handleCloseActionMenu();
        }}>
          <ReplyIcon sx={{ mr: 1 }} /> Reply via Email
        </MenuItem>
        {actionMenuInquiry && !actionMenuInquiry.linked_client && (
          <MenuItem onClick={() => {
            handleOpenLinkDialog(actionMenuInquiry);
            handleCloseActionMenu();
          }}>
            <PersonAddIcon sx={{ mr: 1 }} /> Link to Client
          </MenuItem>
        )}
        {actionMenuInquiry && actionMenuInquiry.status !== 'completed' && (
          <MenuItem onClick={() => {
            handleStatusUpdate(actionMenuInquiry.id, 'completed');
            handleCloseActionMenu();
          }}>
            <DoneAllIcon sx={{ mr: 1 }} /> Mark as Completed
          </MenuItem>
        )}
        {actionMenuInquiry && actionMenuInquiry.status !== 'archived' && (
          <MenuItem onClick={() => handleArchive(actionMenuInquiry.id)}>
            <ArchiveIcon sx={{ mr: 1 }} /> Archive
          </MenuItem>
        )}
      </Menu>

      {/* Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
      >
        {selectedInquiry && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" fontWeight="bold">
                  Inquiry from {selectedInquiry.client_name}
                </Typography>
                <IconButton onClick={handleCloseDialog}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Chip
                  icon={getStatusIcon(selectedInquiry.status)}
                  label={selectedInquiry.status ? selectedInquiry.status.charAt(0).toUpperCase() + selectedInquiry.status.slice(1) : 'New'}
                  sx={{
                    bgcolor: alpha(getStatusColor(selectedInquiry.status), 0.1),
                    color: getStatusColor(selectedInquiry.status)
                  }}
                />
                {selectedInquiry.linked_client && (
                  <Chip
                    icon={<LinkIcon />}
                    label={`Linked to ${selectedInquiry.linked_client.name}`}
                    color="success"
                    variant="outlined"
                    onClick={() => {
                      handleCloseDialog();
                      navigate(`/clients/${selectedInquiry.linked_client.id}`);
                    }}
                  />
                )}
              </Stack>
            </DialogTitle>

            <DialogContent dividers>
              {/* Client Information */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Client Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Name</Typography>
                    <Typography variant="body1">{selectedInquiry.client_name}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Email</Typography>
                    <Typography variant="body1">
                      <a href={`mailto:${selectedInquiry.client_email}`}>
                        {selectedInquiry.client_email}
                      </a>
                    </Typography>
                  </Grid>
                  {selectedInquiry.client_phone && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Phone</Typography>
                      <Typography variant="body1">
                        <a href={`tel:${selectedInquiry.client_phone}`}>
                          {selectedInquiry.client_phone}
                        </a>
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Submitted</Typography>
                    <Typography variant="body1">{formatDate(selectedInquiry.created_at)}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Project Details */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Project Details
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Description
                </Typography>
                <Typography variant="body1" paragraph>
                  {selectedInquiry.project_description}
                </Typography>

                <Grid container spacing={2} sx={{ mt: 2 }}>
                  {selectedInquiry.placement && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Placement</Typography>
                      <Typography variant="body1">{selectedInquiry.placement}</Typography>
                    </Grid>
                  )}
                  
                  {selectedInquiry.size_estimate && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Size</Typography>
                      <Typography variant="body1">{selectedInquiry.size_estimate}</Typography>
                    </Grid>
                  )}

                  {selectedInquiry.budget_range && (
                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <MoneyIcon color="success" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Budget Range
                            </Typography>
                            <Typography variant="body2" fontWeight="600">
                              {formatBudget(selectedInquiry.budget_range)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>
                  )}

                  {selectedInquiry.preferred_timeframe && (
                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <ScheduleIcon color="info" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Timeframe
                            </Typography>
                            <Typography variant="body2" fontWeight="600">
                              {formatTimeframe(selectedInquiry.preferred_timeframe)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>
                  )}
                </Grid>

                {selectedInquiry.is_first_tattoo && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    This will be their first tattoo
                  </Alert>
                )}

                {selectedInquiry.additional_notes && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Additional Notes
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Typography variant="body2">
                        {selectedInquiry.additional_notes}
                      </Typography>
                    </Paper>
                  </Box>
                )}
              </Paper>

              {/* Reference Images */}
              {selectedInquiry.reference_images?.length > 0 && (
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GalleryIcon />
                    Reference Images ({selectedInquiry.reference_images.length})
                  </Typography>
                  <ImageList cols={3} gap={8}>
                    {selectedInquiry.reference_images.map((image, index) => (
                      <ImageListItem 
                        key={image.id || index}
                        sx={{ 
                          cursor: 'pointer',
                          borderRadius: 1,
                          overflow: 'hidden',
                          '&:hover': { opacity: 0.8 }
                        }}
                        onClick={() => handleOpenImagePreview(selectedInquiry.reference_images, index)}
                      >
                        <img
                          src={image.url || image}
                          alt={`Reference ${index + 1}`}
                          loading="lazy"
                          style={{ aspectRatio: '1', objectFit: 'cover' }}
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </Paper>
              )}

              {/* Custom Form Data */}
              {selectedInquiry.custom_form_data && Object.keys(selectedInquiry.custom_form_data).length > 0 && (
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Additional Form Responses
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries(selectedInquiry.custom_form_data).map(([key, value]) => (
                      <Grid item xs={12} sm={6} key={key}>
                        <Typography variant="body2" color="text.secondary">
                          {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Typography>
                        <Typography variant="body1">
                          {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              )}
            </DialogContent>

            <DialogActions sx={{ p: 3, gap: 2 }}>
              {/* FIXED: Simplified onClick - handleOpenLinkDialog now handles closing the detail dialog */}
              {!selectedInquiry.linked_client && (
                <Button
                  variant="outlined"
                  startIcon={<PersonAddIcon />}
                  onClick={() => handleOpenLinkDialog(selectedInquiry)}
                >
                  Link to Client
                </Button>
              )}
              
              <Button
                variant="contained"
                startIcon={<ReplyIcon />}
                onClick={() => handleReplyToClient(selectedInquiry)}
                sx={{ flex: 1 }}
              >
                Reply to {selectedInquiry.client_name}
              </Button>

              {selectedInquiry.client_phone && (
                <Button
                  variant="outlined"
                  startIcon={<PhoneIcon />}
                  href={`tel:${selectedInquiry.client_phone}`}
                >
                  Call
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Link to Client Dialog */}
      <Dialog
        open={linkDialogOpen}
        onClose={handleCloseLinkDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Link Inquiry to Client
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Tabs 
              value={linkMode === 'existing' ? 0 : 1} 
              onChange={(e, v) => setLinkMode(v === 0 ? 'existing' : 'new')}
              sx={{ mb: 3 }}
            >
              <Tab label="Existing Client" />
              <Tab label="Create New Client" />
            </Tabs>

            {linkMode === 'existing' ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Search and select an existing client to link this inquiry to:
                </Typography>
                <Autocomplete
                  options={Array.isArray(clients) ? clients : []}
                  loading={loadingClients}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    return `${option.first_name || ''} ${option.last_name || ''} (${option.email || ''})`;
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return option.id === value.id;
                  }}
                  value={selectedClient}
                  onChange={(e, value) => setSelectedClient(value)}
                  onInputChange={(e, value, reason) => {
                    // Only fetch when user types, not on selection
                    if (reason === 'input') {
                      fetchClients(value);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Clients"
                      placeholder="Type to search..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingClients ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <li key={key} {...otherProps}>
                        <Stack>
                          <Typography variant="body1">
                            {option?.first_name || ''} {option?.last_name || ''}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option?.email || ''}
                          </Typography>
                        </Stack>
                      </li>
                    );
                  }}
                />
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Create a new client from this inquiry's information:
                </Typography>
                {linkingInquiry && (
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Name</Typography>
                        <Typography variant="body1" fontWeight="600">
                          {linkingInquiry.client_name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Email</Typography>
                        <Typography variant="body1">{linkingInquiry.client_email}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Phone</Typography>
                        <Typography variant="body1">
                          {linkingInquiry.client_phone || 'Not provided'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseLinkDialog}>Cancel</Button>
          {linkMode === 'existing' ? (
            <Button
              variant="contained"
              onClick={handleLinkToClient}
              disabled={!selectedClient}
              startIcon={<LinkIcon />}
            >
              Link Client
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleCreateNewClient}
              startIcon={<PersonAddIcon />}
            >
              Create & Link Client
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={imagePreviewOpen}
        onClose={handleCloseImagePreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography>
              Reference Image {currentImageIndex + 1} of {previewImages.length}
            </Typography>
            <IconButton onClick={handleCloseImagePreview}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {previewImages.length > 0 && (
            <Box sx={{ textAlign: 'center' }}>
              <img
                src={previewImages[currentImageIndex]?.url || previewImages[currentImageIndex]}
                alt={`Reference ${currentImageIndex + 1}`}
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              />
            </Box>
          )}
        </DialogContent>
        {previewImages.length > 1 && (
          <DialogActions sx={{ justifyContent: 'center' }}>
            <Button
              disabled={currentImageIndex === 0}
              onClick={() => setCurrentImageIndex(i => i - 1)}
            >
              Previous
            </Button>
            <Button
              disabled={currentImageIndex === previewImages.length - 1}
              onClick={() => setCurrentImageIndex(i => i + 1)}
            >
              Next
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Container>
  );
};

export default TattooInquiries;